import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  FileText, 
  Search, 
  Calendar, 
  Clock,
  User,
  Bot,
  Play,
  Pause,
  Download,
  Eye,
  Filter,
  Heart,
  Scale,
  HeartHandshake,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { cn } from '@/lib/utils';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IntakeLogs = () => {
  const [logs, setLogs] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [sessionInsights, setSessionInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTenant, setSelectedTenant] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [dateRange, setDateRange] = useState('all');

  const industryIcons = {
    Medical: Heart,
    Legal: Scale,
    AgedCare: HeartHandshake
  };

  const industryColors = {
    Medical: '#EF4444',
    Legal: '#3B82F6',
    AgedCare: '#10B981'
  };

  const statusColors = {
    completed: 'bg-green-500/20 text-green-400 border-green-500/30',
    active: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    failed: 'bg-red-500/20 text-red-400 border-red-500/30',
    pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
  };

  useEffect(() => {
    fetchLogsAndTenants();
  }, []);

  const fetchLogsAndTenants = async () => {
    try {
      setLoading(true);
      const [logsRes, tenantsRes] = await Promise.allSettled([
        axios.get(`${API}/intakes`),
        axios.get(`${API}/tenants`)
      ]);

      if (logsRes.status === 'fulfilled') {
        setLogs(logsRes.value.data || []);
      }

      if (tenantsRes.status === 'fulfilled') {
        setTenants(tenantsRes.value.data || []);
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load intake logs');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchLogsAndTenants();
  };

  const handleViewDetails = async (log) => {
    setSelectedLog(log);
    setIsDetailDialogOpen(true);
    // Fetch the AI-generated structured summary (learning triplet) for this session
    setSessionInsights(null);
    const sid = log.session_id;
    if (sid) {
      setInsightsLoading(true);
      try {
        const res = await axios.get(`${API}/learning/triplets/${sid}`);
        setSessionInsights(res.data?.structured_data || res.data?.ai_original_summary || null);
      } catch (e) {
        // No triplet yet (e.g., session still processing) — silently ignore
        setSessionInsights(null);
      } finally {
        setInsightsLoading(false);
      }
    }
  };

  const handleExportLog = async (logId) => {
    try {
      const response = await axios.get(`${API}/intakes/${logId}/export`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `intake_log_${logId}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exporting log:', err);
      setError('Failed to export log');
    }
  };

  // Filter logs based on search and filters
  const filteredLogs = logs.filter(log => {
    const matchesSearch = !searchTerm || 
      log.tenant?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.session_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTenant = selectedTenant === 'all' || log.tenant_id === selectedTenant;
    const matchesStatus = selectedStatus === 'all' || log.status === selectedStatus;
    
    let matchesDate = true;
    if (dateRange !== 'all' && log.created_at) {
      const logDate = new Date(log.created_at);
      const now = new Date();
      const diffDays = Math.floor((now - logDate) / (1000 * 60 * 60 * 24));
      
      switch (dateRange) {
        case 'today':
          matchesDate = diffDays === 0;
          break;
        case 'week':
          matchesDate = diffDays <= 7;
          break;
        case 'month':
          matchesDate = diffDays <= 30;
          break;
      }
    }
    
    return matchesSearch && matchesTenant && matchesStatus && matchesDate;
  });

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'active':
        return <Play className="w-4 h-4" />;
      case 'failed':
        return <XCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 'N/A';
    
    const duration = Math.floor((new Date(endTime) - new Date(startTime)) / 1000);
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48 bg-slate-700" />
          <Skeleton className="h-10 w-32 bg-slate-700" />
        </div>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6">
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} className="h-16 w-full bg-slate-700" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">Intake Logs</h1>
          <p className="text-sm text-slate-400">View and manage all voice intake session records</p>
        </div>
        
        <Button 
          onClick={handleRefresh}
          className="bg-teal-500 hover:bg-teal-600 text-white w-full sm:w-auto"
          data-testid="refresh-logs-button"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert className="bg-red-500/10 border-red-500/20 text-red-400">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center">
            <Filter className="w-5 h-5 mr-2" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by tenant or session..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-slate-700 border-slate-600 text-white"
                  data-testid="search-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Tenant</label>
              <Select value={selectedTenant} onValueChange={setSelectedTenant}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="tenant-filter">
                  <SelectValue placeholder="All tenants" />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="all" className="text-white hover:bg-slate-600">All Tenants</SelectItem>
                  {tenants.map(tenant => {
                    const IndustryIcon = industryIcons[tenant.industry_type] || FileText;
                    return (
                      <SelectItem key={tenant.id} value={tenant.id} className="text-white hover:bg-slate-600">
                        <div className="flex items-center space-x-2">
                          <IndustryIcon className="w-4 h-4" style={{ color: industryColors[tenant.industry_type] }} />
                          <span>{tenant.name}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Status</label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="status-filter">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="all" className="text-white hover:bg-slate-600">All Statuses</SelectItem>
                  <SelectItem value="completed" className="text-white hover:bg-slate-600">Completed</SelectItem>
                  <SelectItem value="active" className="text-white hover:bg-slate-600">Active</SelectItem>
                  <SelectItem value="failed" className="text-white hover:bg-slate-600">Failed</SelectItem>
                  <SelectItem value="pending" className="text-white hover:bg-slate-600">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Date Range</label>
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="date-filter">
                  <SelectValue placeholder="All time" />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="all" className="text-white hover:bg-slate-600">All Time</SelectItem>
                  <SelectItem value="today" className="text-white hover:bg-slate-600">Today</SelectItem>
                  <SelectItem value="week" className="text-white hover:bg-slate-600">Last 7 Days</SelectItem>
                  <SelectItem value="month" className="text-white hover:bg-slate-600">Last 30 Days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center justify-between">
            <span>Intake Sessions</span>
            <Badge variant="outline" className="border-slate-600 text-slate-300">
              {filteredLogs.length} of {logs.length} sessions
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredLogs.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-slate-400 mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No intake logs found</h3>
              <p className="text-slate-400">
                {logs.length === 0 
                  ? "No voice intake sessions have been recorded yet" 
                  : "Try adjusting your filters to see more results"
                }
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700 hover:bg-slate-700/50">
                  <TableHead className="text-slate-300">Session</TableHead>
                  <TableHead className="text-slate-300">Tenant</TableHead>
                  <TableHead className="text-slate-300">Status</TableHead>
                  <TableHead className="text-slate-300">Duration</TableHead>
                  <TableHead className="text-slate-300">Messages</TableHead>
                  <TableHead className="text-slate-300">Created</TableHead>
                  <TableHead className="text-slate-300">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.map((log) => {
                  const tenant = tenants.find(t => t.id === log.tenant_id);
                  const IndustryIcon = industryIcons[tenant?.industry] || FileText;
                  
                  return (
                    <TableRow 
                      key={log.id} 
                      className="border-slate-700 hover:bg-slate-700/30"
                      data-testid={`log-row-${log.id}`}
                    >
                      <TableCell>
                        <div className="space-y-1">
                          <p className="font-medium text-white text-sm">
                            {log.session_id?.slice(0, 8) || 'Unknown'}...
                          </p>
                          <p className="text-xs text-slate-400">ID: {log.id}</p>
                        </div>
                      </TableCell>
                      
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <IndustryIcon 
                            className="w-4 h-4" 
                            style={{ color: industryColors[tenant?.industry] || '#6B7280' }} 
                          />
                          <span className="text-sm text-slate-300">
                            {tenant?.name || 'Unknown Tenant'}
                          </span>
                        </div>
                      </TableCell>
                      
                      <TableCell>
                        <Badge className={cn("px-2 py-1 flex items-center space-x-1 w-fit", 
                          statusColors[log.status?.toLowerCase()] || statusColors.pending)}>
                          {getStatusIcon(log.status)}
                          <span className="text-xs capitalize">
                            {log.status || 'Pending'}
                          </span>
                        </Badge>
                      </TableCell>
                      
                      <TableCell>
                        <div className="flex items-center space-x-1 text-sm text-slate-300">
                          <Clock className="w-3 h-3" />
                          <span>{formatDuration(log.start_time, log.end_time)}</span>
                        </div>
                      </TableCell>
                      
                      <TableCell>
                        <span className="text-sm text-slate-300">
                          {log.transcript?.length || 0} messages
                        </span>
                      </TableCell>
                      
                      <TableCell>
                        <div className="space-y-1">
                          <p className="text-xs text-slate-300">
                            {log.created_at ? new Date(log.created_at).toLocaleDateString() : 'N/A'}
                          </p>
                          <p className="text-xs text-slate-400">
                            {log.created_at ? new Date(log.created_at).toLocaleTimeString() : ''}
                          </p>
                        </div>
                      </TableCell>
                      
                      <TableCell>
                        <div className="flex items-center space-x-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewDetails(log)}
                            className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
                            data-testid={`view-log-${log.id}`}
                          >
                            <Eye className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleExportLog(log.id)}
                            className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
                            data-testid={`export-log-${log.id}`}
                          >
                            <Download className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <FileText className="w-5 h-5" />
              <span>Session Details</span>
              {selectedLog && (
                <Badge className={cn("px-2 py-1", 
                  statusColors[selectedLog.status?.toLowerCase()] || statusColors.pending)}>
                  {selectedLog.status || 'Pending'}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-6">
              {/* Session Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-700/50 rounded-lg">
                <div>
                  <label className="text-xs text-slate-400">Session ID</label>
                  <p className="text-sm text-white font-mono">{selectedLog.session_id}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-400">Tenant</label>
                  <p className="text-sm text-white">
                    {tenants.find(t => t.id === selectedLog.tenant_id)?.name || 'Unknown'}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-slate-400">Duration</label>
                  <p className="text-sm text-white">
                    {formatDuration(selectedLog.start_time, selectedLog.end_time)}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-slate-400">Created</label>
                  <p className="text-sm text-white">
                    {selectedLog.created_at ? new Date(selectedLog.created_at).toLocaleString() : 'N/A'}
                  </p>
                </div>
              </div>

              {/* AI Insights (structured summary from the Learning Loop) */}
              <div>
                <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <Bot className="w-5 h-5 text-teal-400" /> AI Insights
                </h4>
                {insightsLoading ? (
                  <p className="text-sm text-slate-400">Loading insights…</p>
                ) : sessionInsights && Object.keys(sessionInsights).length > 0 ? (
                  <div className="rounded-lg border border-slate-600 bg-slate-700/30 p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {Object.entries(sessionInsights).map(([key, value]) => {
                      if (value === null || value === undefined || value === '' ||
                        (Array.isArray(value) && value.length === 0)) return null;
                      const label = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                      let display;
                      if (Array.isArray(value)) display = value.join(', ');
                      else if (typeof value === 'object') display = JSON.stringify(value);
                      else display = String(value);
                      const isFlag = /triage|emergency|severity|risk|escalation/i.test(key);
                      return (
                        <div key={key} className="text-sm">
                          <span className="text-xs text-slate-400 block">{label}</span>
                          <span className={cn('text-white', isFlag && /emergency|severe|urgent|high/i.test(display) && 'text-red-300 font-medium')}>
                            {display}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rounded-lg border border-slate-600 bg-slate-700/30 p-4 text-sm text-slate-400">
                    No AI summary available for this session yet (generated automatically after a completed voice session).
                  </div>
                )}
              </div>

              {/* Transcript */}
              <div>
                <h4 className="text-lg font-semibold text-white mb-4">Conversation Transcript</h4>
                <ScrollArea className="h-64 w-full rounded-lg border border-slate-600 p-4 bg-slate-700/30">
                  {selectedLog.transcript && selectedLog.transcript.length > 0 ? (
                    <div className="space-y-4">
                      {selectedLog.transcript.map((message, index) => (
                        <div
                          key={index}
                          className={cn(
                            "flex",
                            message.speaker === 'agent' ? 'justify-start' : 'justify-end'
                          )}
                        >
                          <div
                            className={cn(
                              "max-w-xs lg:max-w-sm px-3 py-2 rounded-lg",
                              message.speaker === 'agent'
                                ? 'bg-slate-600 text-white'
                                : 'bg-teal-500 text-white'
                            )}
                          >
                            <div className="flex items-center space-x-2 mb-1">
                              {message.speaker === 'agent' ? (
                                <Bot className="w-3 h-3" />
                              ) : (
                                <User className="w-3 h-3" />
                              )}
                              <span className="text-xs opacity-75 capitalize">
                                {message.speaker}
                              </span>
                              {message.timestamp && (
                                <span className="text-xs opacity-50">
                                  {new Date(message.timestamp).toLocaleTimeString()}
                                </span>
                              )}
                            </div>
                            <p className="text-sm">{message.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-slate-400 py-8">
                      <Bot className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No transcript available</p>
                    </div>
                  )}
                </ScrollArea>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IntakeLogs;